import type { ReaderType } from "@pages/analytics/types";
import { useAppDispatch } from "@store";
import { closeDialog, updateDialog } from "@store/dialogs";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { SessionDetailsHeader } from "./session-details-header";
import { SessionDetailsStats } from "./session-details-stats";
import { SessionNavigation } from "./session-navigation";
import { SessionPerformanceOverview } from "./session-performance-overview";

type TSessionDetailsProps = {
  session: ReaderType;
  pageTitle: string;
  versionTitle: string;
  storyId: string;
  versionId: string;
  allSessions?: ReaderType[];
};

const DIALOG_ID = "session-details";

export const SessionDetails = ({
  session,
  pageTitle,
  versionTitle,
  storyId,
  versionId,
  allSessions = [],
}: TSessionDetailsProps) => {
  const dispatch = useAppDispatch();
  const close = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  // Find all sessions for the same visitor, sorted by session_index
  const visitorSessions = useMemo(() => {
    return allSessions
      .filter((s) => s.visitor_id === session.visitor_id)
      .sort((a, b) => a.session_index - b.session_index);
  }, [allSessions, session.visitor_id]);

  // Find current session index in the visitor's sessions
  const currentSessionIndexInList = useMemo(() => {
    return visitorSessions.findIndex(
      (s) => s.session_id === session.session_id,
    );
  }, [visitorSessions, session.session_id]);

  const hasPrevSession = currentSessionIndexInList > 0;
  const hasNextSession = currentSessionIndexInList < visitorSessions.length - 1;

  const handlePrevSession = useCallback(() => {
    if (hasPrevSession) {
      const prevSession = visitorSessions[currentSessionIndexInList - 1];
      dispatch(
        updateDialog({
          id: DIALOG_ID,
          props: {
            session: prevSession,
            pageTitle,
            versionTitle,
            storyId,
            versionId,
            allSessions,
          },
        }),
      );
    }
  }, [
    hasPrevSession,
    visitorSessions,
    currentSessionIndexInList,
    dispatch,
    pageTitle,
    versionTitle,
    storyId,
    versionId,
    allSessions,
  ]);

  const handleNextSession = useCallback(() => {
    if (hasNextSession) {
      const nextSession = visitorSessions[currentSessionIndexInList + 1];
      dispatch(
        updateDialog({
          id: DIALOG_ID,
          props: {
            session: nextSession,
            pageTitle,
            versionTitle,
            storyId,
            versionId,
            allSessions,
          },
        }),
      );
    }
  }, [
    hasNextSession,
    visitorSessions,
    currentSessionIndexInList,
    dispatch,
    pageTitle,
    versionTitle,
    storyId,
    versionId,
    allSessions,
  ]);

  return (
    <div className="tw-p-6 -xl:tw-px-2 -xl:tw-py-4">
      <div className="tw-mx-auto tw-flex tw-w-[1106px] tw-flex-col tw-gap-6 tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-xl -xl:tw-w-full -xl:tw-rounded-lg -xl:tw-px-4 -xl:tw-pb-4 -xl:tw-pt-6 -lg:tw-gap-4">
        <SessionDetailsHeader
          readerNumber={session.visitor_index}
          sessionNumber={session.session_index}
          readerInfo={{
            name: session.reader_name,
            email: session.reader_email,
            position: session.reader_position,
            customFields: session.reader_custom_fields,
          }}
          storyName={pageTitle}
          versionName={versionTitle}
          singleSessionVisitor={session.single_session_visitor}
          onClose={close}
        />
        {/* <AIInsights /> */}
        <SessionDetailsStats
          sessionStarted={moment
            .utc(session.timestamp)
            .local()
            .format("D MMM · h:mm A")}
          readingTime={session.duration}
          device={session.device}
          trafficType={session.traffic_type}
          botSignature={session.bot_signature}
          location={`${session.city ? `${session.city}, ` : ""}${session.country}`}
        />
        <SessionPerformanceOverview
          overrides={{ storyId, versionId, sessionId: session.session_id }}
        />
        <SessionNavigation
          onPrevSession={handlePrevSession}
          onNextSession={handleNextSession}
          hasPrevSession={hasPrevSession}
          hasNextSession={hasNextSession}
          sessionNumber={session.session_index}
          singleSessionVisitor={session.single_session_visitor}
        />
      </div>
    </div>
  );
};

SessionDetails.id = DIALOG_ID;
